import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  output,
  signal,
  TemplateRef,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, merge, fromEvent, debounceTime, takeUntil } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'lib-file-drop',
  imports: [NgTemplateOutlet, TranslateModule],
  templateUrl: './file-drop.component.html',
  styleUrl: './file-drop.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(dragover)': 'handleDragOver($event)',
    '(drop)': 'handleDrop($event)',
  },
})
export class FileDropComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly draggingPlaceholder = input<TemplateRef<unknown>>();

  readonly filesDropped = output<FileList>();

  protected readonly isDraggingFile = signal(false);

  private readonly draggedIn$ = new Subject<void>();

  private readonly draggedOut$ = new Subject<void>();

  protected handleDragOver(event: DragEvent): void {
    if (!event.target || !event.currentTarget) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.isDraggingFile.set(true);
    this.draggedIn$.next();

    const unsubscribe$ = merge(this.draggedIn$, this.draggedOut$);

    fromEvent<DragEvent>(event.currentTarget, 'dragleave')
      .pipe(debounceTime(100), takeUntil(unsubscribe$), takeUntilDestroyed(this.destroyRef))
      .subscribe((dragleaveEvent) => {
        dragleaveEvent.stopPropagation();
        dragleaveEvent.preventDefault();

        this.isDraggingFile.set(false);
        this.draggedOut$.next();
      });
  }

  protected handleDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;

    if (files && files.length > 0) {
      this.filesDropped.emit(files);
    }

    this.isDraggingFile.set(false);
    this.draggedOut$.next();
  }
}
